import React, { useState, useEffect } from "react";
import { createPageUrl } from "@/utils";
import { Link } from "react-router-dom";
import { db } from "@/api/db";
import { Plus, Edit, Trash2, ArrowLeft, BookOpen, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export default function AdminLevels() {
  const urlParams = new URLSearchParams(window.location.search);
  const trackId = urlParams.get("track_id");

  const [track, setTrack] = useState(null);
  const [levels, setLevels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: "", description: "", order: 1, is_published: false });
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (trackId) loadData(); }, [trackId]);

  async function loadData() {
    const [tracks, lvls] = await Promise.all([
      db.entities.CareerTrack.filter({ id: trackId }),
      db.entities.Level.filter({ track_id: trackId }, "order"),
    ]);
    setTrack(tracks[0] || null);
    setLevels(lvls);
    setLoading(false);
  }

  function openCreate() {
    setEditing(null);
    setForm({ title: "", description: "", order: levels.length + 1, is_published: false });
    setShowForm(true);
  }

  function openEdit(lvl) {
    setEditing(lvl);
    setForm({ title: lvl.title || "", description: lvl.description || "", order: lvl.order || 1, is_published: !!lvl.is_published });
    setShowForm(true);
  }

  async function handleSave() {
    setSaving(true);
    const data = { ...form, track_id: trackId, order: Number(form.order) };
    if (editing) {
      await db.entities.Level.update(editing.id, data);
    } else {
      await db.entities.Level.create(data);
    }
    await loadData();
    setShowForm(false);
    setSaving(false);
  }

  async function deleteLevel(lvl) {
    if (!window.confirm(`Excluir o nível "${lvl.title}"?`)) return;
    await db.entities.Level.delete(lvl.id);
    await loadData();
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div>
        <Link to={createPageUrl("AdminTracks")} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 mb-3">
          <ArrowLeft className="w-3.5 h-3.5" /> Voltar às trilhas
        </Link>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Níveis — {track?.title}</h1>
            <p className="text-gray-500 mt-1">{levels.length} nível(is) cadastrado(s)</p>
          </div>
          <Button onClick={openCreate} className="bg-emerald-500 hover:bg-emerald-600 gap-2">
            <Plus className="w-4 h-4" /> Novo Nível
          </Button>
        </div>
      </div>

      {levels.length === 0 ? (
        <Card className="border-0 shadow-sm">
          <CardContent className="p-16 text-center text-gray-400">
            <BookOpen className="w-10 h-10 mx-auto mb-3 opacity-30" />
            <p>Nenhum nível criado ainda.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {levels.map(lvl => (
            <Card key={lvl.id} className="border-0 shadow-sm">
              <CardContent className="p-5 flex items-center gap-4">
                <div className="w-9 h-9 bg-gray-100 rounded-full flex items-center justify-center font-bold text-gray-600 flex-shrink-0">
                  {lvl.order}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-semibold text-gray-900">{lvl.title}</p>
                    <Badge className={`text-xs border-0 ${lvl.is_published ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-500"}`}>
                      {lvl.is_published ? "Publicado" : "Rascunho"}
                    </Badge>
                  </div>
                  <p className="text-gray-400 text-sm truncate">{lvl.description}</p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Link to={createPageUrl("AdminLessons") + `?level_id=${lvl.id}&track_id=${trackId}`}>
                    <Button size="sm" variant="outline" className="h-8 gap-1.5 text-xs">
                      <BookOpen className="w-3 h-3" /> Aulas
                    </Button>
                  </Link>
                  <Link to={createPageUrl("AdminExam") + `?level_id=${lvl.id}&track_id=${trackId}`}>
                    <Button size="sm" variant="outline" className="h-8 gap-1.5 text-xs">
                      <FileText className="w-3 h-3" /> Prova
                    </Button>
                  </Link>
                  <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => openEdit(lvl)}>
                    <Edit className="w-4 h-4 text-gray-400" />
                  </Button>
                  <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => deleteLevel(lvl)}>
                    <Trash2 className="w-4 h-4 text-red-400" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Form Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar Nível" : "Novo Nível"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-700">Título *</label>
              <Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Ex: Júnior" className="mt-1" />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Descrição</label>
              <Textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} className="mt-1 resize-none" rows={2} />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Ordem</label>
              <Input type="number" value={form.order} onChange={e => setForm(f => ({ ...f, order: e.target.value }))} className="mt-1 w-24" />
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" id="pub" checked={form.is_published} onChange={e => setForm(f => ({ ...f, is_published: e.target.checked }))} className="accent-emerald-500" />
              <label htmlFor="pub" className="text-sm text-gray-700">Publicar</label>
            </div>
            <div className="flex gap-3 pt-2">
              <Button variant="outline" onClick={() => setShowForm(false)} className="flex-1">Cancelar</Button>
              <Button onClick={handleSave} disabled={!form.title || saving} className="flex-1 bg-emerald-500 hover:bg-emerald-600">
                {saving ? "Salvando..." : "Salvar"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}